// @ts-nocheck
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from './icons';
import Button from './Button';

const SIZE_CLASS = {
  sm: 'max-w-[360px]',
  md: 'max-w-[460px]',
  lg: 'max-w-[640px]',
  xl: 'max-w-[880px]',
};

/**
 * Modal — overlay dialog with header (icon + title + sub + close), body, and
 * optional footer with action buttons. Closes on ESC and scrim click unless
 * `dismissable={false}`.
 *
 * Use `<Modal open onClose={…}><Modal.Body>…</Modal.Body></Modal>` style or
 * pass `title`, `sub`, `icon`, `actions` for the standard layout.
 */
const Modal = ({
  open,
  onClose,
  title,
  sub,
  icon: Icon,
  actions,
  size = 'md',
  dismissable = true,
  closeOnScrim = true,
  className = '',
  children,
}) => {
  useEffect(() => {
    if (!open || !dismissable) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, dismissable, onClose]);

  if (!open) return null;

  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="mov-modal-scrim fixed inset-0 z-[1000] flex items-center justify-center p-4"
      onClick={(e) => {
        if (closeOnScrim && dismissable && e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div className={`mov-modal-panel relative w-full ${sizeClass} ${className}`}>
        {(title || sub || Icon || dismissable) && (
          <header className="flex items-center gap-3 pt-[18px] pb-[14px] px-5 border-b border-mov-line">
            {Icon && (
              <span className="grid place-items-center shrink-0 w-9 h-9 rounded-[10px] bg-mov-green-soft text-mov-green-ink">
                <Icon size={16} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              {title && <h4 className="m-0 mb-0.5 text-[15px] font-semibold">{title}</h4>}
              {sub && <p className="m-0 text-[13px] text-mov-ink-soft">{sub}</p>}
            </div>
            {dismissable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="bg-transparent border-0 p-0 text-mov-ink-mute hover:text-mov-ink cursor-pointer"
              >
                <XIcon size={18} />
              </button>
            )}
          </header>
        )}
        <div className="p-5">{children}</div>
        {actions && (
          <div className="flex gap-2 justify-end py-[14px] px-5 bg-mov-paper-2 border-t border-mov-line">
            {actions}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * ModalActions — convenience footer with a cancel + primary button.
 */
export const ModalActions = ({
  onCancel,
  onConfirm,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  confirmVariant = 'primary',
  confirmLoading = false,
  confirmDisabled = false,
}) => (
  <>
    {onCancel && <Button variant="ghost" onClick={onCancel}>{cancelLabel}</Button>}
    {onConfirm && (
      <Button
        variant={confirmVariant}
        onClick={onConfirm}
        loading={confirmLoading}
        disabled={confirmDisabled}
      >
        {confirmLabel}
      </Button>
    )}
  </>
);

export default Modal;
